import { createApiClient, parseWith, SESSION_SUPERSEDED_ERROR_CODE } from '@imobdesk/api-client'
import type { ResponseParser } from '@imobdesk/api-client'
import { getAccessTokenAsync, isJustLoggedIn } from './token-storage'
import type { AuthSessionInvalidationReason } from '@/lib/glitchtip'
import { refreshToken } from '@/api/auth/auth.actions'
import { API_BASE_URL } from '@/config/api'
import { goToLoginApp } from '@/lib/login-app-url'
import { captureAuthSessionInvalidated } from '@/lib/glitchtip'

let authSessionInvalidated = false
let loginRedirectTriggered = false

const REFRESH_TOKEN_ENDPOINT = '/api/v2/auth/refresh-token'

function sanitizeEndpointForObservability(endpoint: string): string {
  return endpoint.split('?')[0] || endpoint
}

async function invalidateAuthSession(input: {
  endpoint: string
  reason: AuthSessionInvalidationReason
  status?: number
  errorCode?: string
}): Promise<void> {
  captureAuthSessionInvalidated({
    endpoint: sanitizeEndpointForObservability(input.endpoint),
    reason: input.reason,
    status: input.status,
  })

  if (!authSessionInvalidated) {
    authSessionInvalidated = true
    const { clearTokens } = await import('./token-storage')
    clearTokens()
  }

  if (!loginRedirectTriggered) {
    loginRedirectTriggered = true
    const sessionSuperseded =
      input.reason === 'session_superseded' ||
      input.errorCode === SESSION_SUPERSEDED_ERROR_CODE
    goToLoginApp({
      sessionEnded: sessionSuperseded ? 'superseded' : undefined,
    })
  }
}

const client = createApiClient({
  baseUrl: API_BASE_URL,
  entryApp: 'app',
  tokenStorage: { getAccessToken: getAccessTokenAsync },
  refreshTokenEndpoint: REFRESH_TOKEN_ENDPOINT,
  refreshToken: async () => {
    await refreshToken()
  },
  onSessionInvalidated: async (ctx) => {
    // Após handoff/login, evita expulsar o usuário no primeiro 401 transitório.
    if (isJustLoggedIn()) {
      return false
    }
    await invalidateAuthSession({
      endpoint: ctx.endpoint,
      reason: ctx.reason as AuthSessionInvalidationReason,
      status: ctx.status,
      errorCode: ctx.errorCode,
    })
  },
})

export const apiClient = client.apiClient
export const apiClientJson = client.apiClientJson
export const apiClientJsonParsed = client.apiClientJsonParsed

export type { ResponseParser }
export { parseWith }
